/**
 * Separa a narração de bastidor (o que o assistente estava fazendo) da
 * resposta final entregável.
 *
 * O modelo é instruído a envolver toda a narração de passos em tags
 * `<processo>…</processo>` (pode haver várias, intercaladas). Esta função:
 *  - remove esses blocos do texto da resposta;
 *  - junta o conteúdo deles no campo `processo`;
 *  - lida com streaming: um bloco `<processo>` ainda aberto (sem fechamento)
 *    é tratado como narração em andamento e some da resposta; um fragmento
 *    de tag de abertura no fim (`<proc`) também é escondido.
 */

const OPEN = "<processo>";
const CLOSE = "</processo>";

export interface ProcessoSplit {
  /** Resposta final, sem a narração de processo. */
  answer: string;
  /** Narração de bastidor concatenada (pode ser vazia). */
  processo: string;
  /** Há narração de processo em andamento (bloco aberto e ainda não fechado). */
  streaming: boolean;
}

/**
 * Se `text` termina com um prefixo parcial de `tag` (ex.: "<", "<proc"),
 * devolve o texto sem esse fragmento. Evita mostrar meia-tag durante o stream.
 */
function stripPartialOpenTail(text: string): string {
  // maior sufixo de `text` que é prefixo próprio de OPEN
  const max = Math.min(text.length, OPEN.length - 1);
  for (let len = max; len > 0; len--) {
    const suffix = text.slice(text.length - len);
    if (OPEN.startsWith(suffix)) {
      return text.slice(0, text.length - len);
    }
  }
  return text;
}

export function splitProcesso(raw: string): ProcessoSplit {
  if (!raw) return { answer: "", processo: "", streaming: false };

  let answer = "";
  const proc: string[] = [];
  let streaming = false;
  let i = 0;

  while (i < raw.length) {
    const open = raw.indexOf(OPEN, i);
    if (open === -1) {
      answer += raw.slice(i);
      break;
    }
    answer += raw.slice(i, open);
    const close = raw.indexOf(CLOSE, open + OPEN.length);
    if (close === -1) {
      // bloco aberto ainda sem fechamento: narração em andamento
      proc.push(raw.slice(open + OPEN.length));
      streaming = true;
      i = raw.length;
      break;
    }
    proc.push(raw.slice(open + OPEN.length, close));
    i = close + CLOSE.length;
  }

  // remove fragmento de tag de abertura ("<pro…") pendurado no fim da resposta
  answer = stripPartialOpenTail(answer);

  return {
    answer: answer.trim(),
    processo: proc.join("\n\n").trim(),
    streaming,
  };
}
